import React, { ChangeEvent, useEffect, useState } from 'react'
import { SettingItem, SettingsCard } from '../../components/settings/SettingsCard'
import { axiosPatch, axiosPost } from '../../util/api'
import { EventParentTypes } from '../../config'
import { Toasts } from '../../components/toaster/Toast'
import { setCurrentUser } from '../../actions/user'
import { useDispatch } from 'react-redux'
import { Input } from '../../components/inputs/Input'
import { isValidNumericInput } from '../../util/numeric_input'
import { Marketplace, Marketplaces } from '../../util/marketplaces'
import type { EntityId } from '../../types/constants'

interface ProfileSettingsSectionProps {
    selectedProfile: Models.Profile
}

export const useSmartBidSettings = (selectedProfile: Models.Profile) => {
    const dispatch = useDispatch()
    const settings = selectedProfile.settings as Models.Settings | null
    const smartBidSettings = selectedProfile
        .smart_bid_settings?.[0] as Models.SmartBidSetting | null
    const [smartBidDefault, setSmartBidDefault] = useState(false)
    const [pretend, setPretend] = useState(false)
    const [defaultProfile, setDefaultProfile] = useState<EntityId | null>(null)

    const [loading, setLoading] = useState(false)
    const [minBid, setMinBid] = useState<number | string>(0)
    const [maxBid, setMaxBid] = useState<number | string>(0)
    const [name, setName] = useState('')
    const [maximumBidAdjustment, setMaximumBidAdjustment] = useState<null | number | string>(null)
    const [lookbackDays, setLookbackDays] = useState<null | number | string>(null)
    const [thresholdTargetACOS, setThresholdTargetACOS] = useState<null | number | string>(null)
    const [dirty, setDirty] = useState(false)

    useEffect(() => {
        if (settings) {
            setDefaultProfile(settings.default_profile)
            setPretend(settings.pretend)
            setSmartBidDefault(settings.smart_bid_default)
        } else {
            setSmartBidDefault(false)
        }

        if (smartBidSettings) {
            // Reinitialize form when settings are updated remotely.
            // min/max bid are nullable — null means "no limit", so show a
            // blank field (not 0) to match the "Leave blank for no limit" copy.
            setMinBid(smartBidSettings.min_bid != null ? smartBidSettings.min_bid / 100.0 : '')
            setMaxBid(smartBidSettings.max_bid != null ? smartBidSettings.max_bid / 100.0 : '')
            setName(smartBidSettings?.name ?? '')
            setLookbackDays(smartBidSettings.lookback_days ?? 0)
            setMaximumBidAdjustment(
                smartBidSettings.maximum_bid_adjustment
                    ? smartBidSettings.maximum_bid_adjustment / 100.0
                    : 0,
            )
            setThresholdTargetACOS(
                smartBidSettings.target_acos_threshold
                    ? smartBidSettings.target_acos_threshold / 100.0
                    : 0,
            )
        } else {
            setMinBid('')
            setMaxBid('')
            setName('')
            setLookbackDays(0)
            setMaximumBidAdjustment(0)
            setThresholdTargetACOS(0)
        }
        setLoading(false)
    }, [selectedProfile.profile_id, settings, smartBidSettings])

    const saveBidsSettings = () => {
        setLoading(true)
        axiosPost(
            `smart-bid-settings`,
            {
                name,
                parent_id: selectedProfile.profile_id,
                parent_type: EventParentTypes.Profile,
                min_bid: minBid ? Math.round(parseFloat('' + minBid) * 100.0) : null,
                max_bid: maxBid ? Math.round(parseFloat('' + maxBid) * 100.0) : null,
                maximum_bid_adjustment: parseFloat('' + maximumBidAdjustment) * 100.0,
                target_acos_threshold: parseFloat('' + thresholdTargetACOS) * 100.0,
                lookback_days: lookbackDays,
            },
            false,
            {
                headers: {
                    ProfileId: selectedProfile.profile_id,
                },
            },
        )
            .then((res) => {
                setLoading(false)
                setDirty(false)
                const data = res.data
                setThresholdTargetACOS(
                    data.target_acos_threshold ? data.target_acos_threshold / 100.0 : 0,
                )
                setMaximumBidAdjustment(
                    data?.maximum_bid_adjustment ? data.maximum_bid_adjustment / 100.0 : 0,
                )

                Toasts.success('Settings saved')
            })
            .catch(() => {
                setLoading(false)
            })
    }

    const saveAccountSettings = (extra: any = undefined) => {
        setLoading(true)
        axiosPatch(`settings/${settings?.id}/account`, {
            pretend,
            smart_bid_default: smartBidDefault,
            default_profile: defaultProfile,
            ...extra,
        })
            .then((res) => {
                setLoading(false)
                Toasts.success('Account settings saved')
                dispatch(setCurrentUser(res.data))
            })
            .catch(() => {
                setLoading(false)
            })
    }

    return {
        dirty,
        setDirty,
        smartBidDefault,
        setSmartBidDefault,
        thresholdTargetACOS,
        setThresholdTargetACOS,
        maximumBidAdjustment,
        setMaximumBidAdjustment,
        lookbackDays,
        setLookbackDays,
        loading,
        name,
        setName,
        minBid,
        setMinBid,
        maxBid,
        setMaxBid,
        saveBidsSettings,
        saveAccountSettings,
        marketplace: Marketplaces[selectedProfile.marketplace_id as Marketplace],
    }
}

export const DefaultSmartBidSettings = ({ selectedProfile }: ProfileSettingsSectionProps) => {
    const {
        loading,
        saveBidsSettings,
        dirty,
        setDirty,
        minBid,
        setMinBid,
        maxBid,
        setMaxBid,
        marketplace,
    } = useSmartBidSettings(selectedProfile)

    const set = (setter: (v: string) => void) => (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        if (!isValidNumericInput(value)) {
            return
        }

        setter(value)
        setDirty(true)
    }

    return (
        <SettingsCard
            title={'Default Bid Limits'}
            onSave={dirty ? saveBidsSettings : null}
            loading={loading}
            description="The default minimum and maximum bid applied to every Merch Jar-managed
        campaign. Campaigns, ad groups, keywords, and targets can each override these with
        their own bid limits."
        >
            <SettingItem
                title="Minimum Bid"
                description="The lowest bid Merch Jar will set through Recipes, Smart Bidding, or
             Bulk Actions. If a bid drops below this on Amazon’s ad console or elsewhere, it
             is raised back to this value on the next operation. Leave blank for no minimum."
                rightComponent={
                    <Input
                        style={{ width: '100px' }}
                        value={minBid}
                        onChange={set(setMinBid)}
                        prefix={marketplace.currency_symbol}
                    />
                }
            />
            <SettingItem
                title="Maximum Bid"
                description="The highest bid Merch Jar will set through Recipes, Smart Bidding, or
             Bulk Actions. If a bid rises above this on Amazon’s ad console or elsewhere, it
             is lowered back to this value on the next operation. Leave blank for no maximum."
                rightComponent={
                    <Input
                        style={{ width: '100px' }}
                        value={maxBid}
                        onChange={set(setMaxBid)}
                        prefix={marketplace.currency_symbol}
                    />
                }
                bottomDivider={false}
            />
        </SettingsCard>
    )
}
